/**
 * usePolledQuery
 *
 * US-24-GR-13 — polls a query function every `intervalMs` milliseconds.
 * Pauses when the tab is hidden (Page Visibility API) and resumes on focus.
 */

import { useState, useEffect, useRef, useCallback, startTransition } from 'react';

interface UsePolledQueryResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  refetch: () => void;
}

export function usePolledQuery<T>(
  queryFn: () => Promise<T>,
  intervalMs = 30_000
): UsePolledQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchNow = useCallback(async () => {
    try {
      const result = await queryFn();
      if (isMountedRef.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [queryFn]);

  const startPolling = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      void fetchNow();
    }, intervalMs);
  }, [fetchNow, intervalMs]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Initial fetch
    startTransition(() => { void fetchNow(); });
    startPolling();

    // Pause/resume on visibility
    function handleVisibility() {
      if (document.hidden) {
        stopPolling();
      } else {
        startTransition(() => { void fetchNow(); });
        startPolling();
      }
    }

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isMountedRef.current = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchNow, startPolling, stopPolling]);

  return { data, error, isLoading, refetch: fetchNow };
}
